/**
 * Schedule Entry Override Dialog
 * Apply a per-row override to an Interest Payment, Combined Payment, or
 * Capital Payment row. The user can change:
 *   * the interest amount (residual rolls into the next payment; WHT recalcs
 *     proportionally using the row's stored wht_rate_applied + wht_gross_up)
 *   * the capital amount (T066 — defers a portion of capital into the next
 *     Combined Payment / Capital Payment row; rejected on equal_installment
 *     contracts)
 *   * the payment date (e.g. public holiday shift)
 *   * any combination of the three (creates one override record per change,
 *     sharing the reason)
 *
 * Rate changes flow through the rate-change workflow; WHT rate changes through
 * the investor stakeholder profile.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { apiGet, apiPost } from '../../utils/apiClient';
import { formatNumber } from '../../utils/formatters';
import { useTenant } from '../../contexts/TenantContext';

const fmtDateInput = (d) => {
  if (!d) return '';
  if (typeof d === 'string') return d.substring(0, 10);
  try { return format(d, 'yyyy-MM-dd'); } catch { return ''; }
};

// Mirror sp_schedule_apply_override_v2's WHT logic. Match the JS
// calculateWHT helper used during schedule generation. Keeps the modal's
// preview in lock-step with what the backend will persist.
const calcWhtPreview = (newGross, whtRate, whtGrossUp) => {
  const rate = parseFloat(whtRate) || 0;
  const grossUp = !!whtGrossUp;
  const newWht = Math.round(newGross * rate) / 100;
  const newNet = grossUp ? newGross : newGross - newWht;
  return { newWht, newNet };
};

const ScheduleEntryOverrideDialog = ({
  open,
  onClose,
  contract,
  scheduleEntry,
  onSubmitSuccess,
}) => {
  const { tenant } = useTenant();
  const dp = tenant?.roundingPrecision ?? 3;

  // Form state
  const [overrideValue, setOverrideValue] = useState('');         // new interest
  const [overrideCapital, setOverrideCapital] = useState('');     // new capital (T066)
  const [overrideDate, setOverrideDate] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Caps fetched from the backend (interest + capital)
  const [maxAmount, setMaxAmount] = useState(null);
  const [maxCapital, setMaxCapital] = useState(null);

  useEffect(() => {
    if (open && scheduleEntry) {
      setOverrideValue('');
      setOverrideCapital('');
      setOverrideDate('');
      setReason('');
      setError(null);
      setMaxAmount(null);
      setMaxCapital(null);

      // T066: backend returns BOTH caps in one response.
      if (contract?.id && scheduleEntry.id) {
        apiGet(`/amendments/contract/${contract.id}/payment-max?entry_id=${scheduleEntry.id}`)
          .then(resp => {
            const data = resp?.data ?? resp;
            const intMax = data?.max_amount;
            const capMax = data?.capital_max_amount;
            if (intMax != null) setMaxAmount(parseFloat(intMax));
            if (capMax != null) setMaxCapital(parseFloat(capMax));
          })
          .catch(err => console.warn('Could not load override caps:', err));
      }
    }
  }, [open, scheduleEntry, contract?.id]);

  // Derived row context
  const originalAmount = scheduleEntry ? parseFloat(scheduleEntry.interest_amount || 0) : null;
  const originalCapital = scheduleEntry ? parseFloat(scheduleEntry.capital_amount || 0) : null;
  const originalWht = scheduleEntry ? parseFloat(scheduleEntry.wht_amount || 0) : null;
  const originalNet = scheduleEntry ? parseFloat(scheduleEntry.net_interest_amount || 0) : null;
  const whtRate = scheduleEntry ? parseFloat(scheduleEntry.wht_rate_applied || 0) : 0;
  const whtGrossUp = scheduleEntry ? !!scheduleEntry.wht_gross_up : false;
  const originalDate = scheduleEntry?.payment_date ? fmtDateInput(scheduleEntry.payment_date) : '';
  const narration = scheduleEntry?.narration;

  // Which override types this row supports
  const isInterestRow = narration === 'Interest Payment' || narration === 'Combined Payment';
  const isCapitalRow = narration === 'Combined Payment' || narration === 'Capital Payment';
  const isEqualInstallment = contract?.equal_installment === true;
  const capitalDisabled = !isCapitalRow || isEqualInstallment;
  const isApproved = contract?.status === 'Approved';

  // Change detection
  const hasAmountChange = isInterestRow
    && overrideValue !== '' && overrideValue !== undefined && overrideValue !== null
    && parseFloat(overrideValue) !== originalAmount;
  const hasCapitalChange = isCapitalRow && !isEqualInstallment
    && overrideCapital !== '' && overrideCapital !== undefined && overrideCapital !== null
    && parseFloat(overrideCapital) !== originalCapital;
  const hasDateChange = !!overrideDate && overrideDate !== originalDate;
  const hasAnyChange = hasAmountChange || hasCapitalChange || hasDateChange;

  // Live previews
  const newAmountNum = parseFloat(overrideValue) || 0;
  const newCapitalNum = parseFloat(overrideCapital) || 0;
  const interestResidual = hasAmountChange ? (originalAmount || 0) - newAmountNum : 0;
  const capitalResidual = hasCapitalChange ? (originalCapital || 0) - newCapitalNum : 0;

  // WHT preview only meaningful when interest changes
  const whtPreview = hasAmountChange ? calcWhtPreview(newAmountNum, whtRate, whtGrossUp) : null;

  // Validation against caps
  const exceedsCap = hasAmountChange && maxAmount != null && newAmountNum > maxAmount + 0.001;
  const exceedsCapitalCap = hasCapitalChange && maxCapital != null && newCapitalNum > maxCapital + 0.001;
  const capitalNegative = hasCapitalChange && newCapitalNum < 0;
  const isValid = reason && hasAnyChange && !exceedsCap && !exceedsCapitalCap && !capitalNegative;

  const handleSubmit = async () => {
    if (!reason) {
      setError('A reason is required');
      return;
    }
    if (!hasAnyChange) {
      setError('Change at least one of interest, capital, or date');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await apiPost(`/amendments/contract/${contract.id}/period-override`, {
        payment_date: scheduleEntry.payment_date?.substring(0, 10),
        narration: scheduleEntry.narration,
        interest_amount_value: hasAmountChange ? parseFloat(overrideValue) : null,
        capital_amount_value: hasCapitalChange ? parseFloat(overrideCapital) : null,
        new_payment_date: hasDateChange ? overrideDate : null,
        reason,
        investor_id: scheduleEntry.investor_id ?? null,
      });

      if (response?.success) {
        onClose();
        if (onSubmitSuccess) {
          onSubmitSuccess(response.data);
        }
      } else {
        setError(response?.message || 'Failed to apply override');
      }
    } catch (err) {
      console.error('Submit error:', err);
      // The API error envelope is { success:false, error: { type, message, ... } }.
      // Prefer the server's explanatory message over the generic Axios one.
      const serverMessage = err.response?.data?.error?.message
        || err.response?.data?.message
        || err.message;
      setError(serverMessage || 'Failed to apply override');
    } finally {
      setSubmitting(false);
    }
  };

  const dialogTitle = narration === 'Capital Payment'
    ? 'Apply Capital Payment Override'
    : narration === 'Combined Payment'
      ? 'Apply Combined Payment Override'
      : 'Apply Interest Payment Override';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon color="primary" />
          <Typography variant="h6">{dialogTitle}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Alert severity={isApproved ? 'warning' : 'info'} sx={{ mb: 2 }}>
          {isApproved
            ? 'This override will be submitted for approval. The schedule will not change until an approver acts on it.'
            : 'This override is applied immediately. It will be discarded if you regenerate the schedule.'}
        </Alert>

        {/* Row breakdown — three-column grid (md=4) so each cell has room. */}
        {scheduleEntry && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
            <Grid container spacing={1}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="caption" color="textSecondary">Payment Date</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {originalDate ? format(parseISO(originalDate), 'dd MMM yyyy') : '-'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="caption" color="textSecondary">Type</Typography>
                <Typography variant="body2" fontWeight="medium">{narration}</Typography>
              </Grid>
              {scheduleEntry.investor_name && (
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="caption" color="textSecondary">Investor</Typography>
                  <Typography variant="body2" fontWeight="medium">{scheduleEntry.investor_name}</Typography>
                </Grid>
              )}
              {isInterestRow && (
                <>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="caption" color="textSecondary">Interest (Gross)</Typography>
                    <Typography variant="body2">R {formatNumber(originalAmount, dp)}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="caption" color="textSecondary">
                      Withholding Tax ({whtGrossUp ? 'Grossed-up' : 'Net'})
                    </Typography>
                    <Typography variant="body2">
                      R {formatNumber(originalWht, dp)}
                      {whtRate > 0 && (
                        <Typography variant="caption" color="textSecondary" sx={{ ml: 0.5 }}>
                          @ {whtRate}%
                        </Typography>
                      )}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="caption" color="textSecondary">Interest (Net)</Typography>
                    <Typography variant="body2">R {formatNumber(originalNet, dp)}</Typography>
                  </Grid>
                </>
              )}
              {isCapitalRow && (
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="caption" color="textSecondary">Capital</Typography>
                  <Typography variant="body2">R {formatNumber(originalCapital, dp)}</Typography>
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="caption" color="textSecondary">Outstanding Balance</Typography>
                <Typography variant="body2">R {formatNumber(scheduleEntry.outstanding_balance, dp)}</Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Adjust amount(s), date, or any combination. At least one change is required.
        </Typography>

        <Grid container spacing={2}>
          {isInterestRow && (
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="New Interest Amount"
                type="number"
                value={overrideValue}
                onChange={(e) => setOverrideValue(e.target.value)}
                fullWidth
                inputProps={{ step: '0.01', max: maxAmount ?? undefined }}
                placeholder={originalAmount != null ? String(originalAmount) : ''}
                error={exceedsCap}
                InputProps={{
                  startAdornment: <Typography variant="body2" sx={{ mr: 0.5 }}>R</Typography>,
                }}
                helperText={
                  exceedsCap
                    ? `Cannot exceed accrued max R ${formatNumber(maxAmount, 2)}`
                    : maxAmount != null
                      ? `Max R ${formatNumber(maxAmount, 2)}. Blank to keep current.`
                      : 'Blank to keep the current amount'
                }
              />
            </Grid>
          )}
          {isCapitalRow && (
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="New Capital Amount"
                type="number"
                value={capitalDisabled && originalCapital != null ? String(originalCapital) : overrideCapital}
                onChange={(e) => setOverrideCapital(e.target.value)}
                fullWidth
                disabled={capitalDisabled}
                inputProps={{ step: '0.01', min: 0, max: maxCapital ?? undefined }}
                placeholder={!capitalDisabled && originalCapital != null ? String(originalCapital) : ''}
                error={exceedsCapitalCap || capitalNegative}
                InputProps={{
                  startAdornment: (
                    <Typography
                      variant="body2"
                      sx={{
                        mr: 0.5,
                        color: capitalDisabled ? 'rgba(0, 0, 0, 0.87)' : 'inherit',
                      }}
                    >
                      R
                    </Typography>
                  ),
                }}
                sx={capitalDisabled ? {
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                    color: 'rgba(0, 0, 0, 0.87)',
                  },
                } : undefined}
                helperText={
                  isEqualInstallment
                    ? 'Not supported on equal-installment'
                    : capitalNegative
                      ? 'Capital cannot be negative'
                      : exceedsCapitalCap
                        ? `Cannot exceed R ${formatNumber(maxCapital, 2)}`
                        : maxCapital != null
                          ? `Max R ${formatNumber(maxCapital, 2)}. Blank to keep current.`
                          : 'Blank to keep the current amount'
                }
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="New Payment Date"
              type="date"
              value={overrideDate}
              onChange={(e) => setOverrideDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Blank to keep the current date"
            />
          </Grid>

          {/* WHT live preview when interest changes */}
          {hasAmountChange && whtRate > 0 && whtPreview && (
            <Grid size={12}>
              <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#f3e5f5' }}>
                <Typography variant="body2">
                  <strong>WHT recalculates with interest:</strong> New WHT R {formatNumber(whtPreview.newWht, 2)} @ {whtRate}%; New Net R {formatNumber(whtPreview.newNet, 2)}
                  {whtGrossUp ? ' (grossed-up — investor receives full gross)' : ''}
                </Typography>
              </Paper>
            </Grid>
          )}

          {/* Interest residual roll-forward */}
          {hasAmountChange && interestResidual !== 0 && (
            <Grid size={12}>
              <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: interestResidual > 0 ? '#fff3e0' : '#e8f5e9' }}>
                <Typography variant="body2">
                  {interestResidual > 0
                    ? `Interest residual of R ${formatNumber(interestResidual, 2)} (under-payment) will roll into the next payment row for this investor — including a proportional WHT shift.`
                    : `Over-payment of R ${formatNumber(Math.abs(interestResidual), 2)} will be applied as a discount to the next payment row.`}
                </Typography>
              </Paper>
            </Grid>
          )}

          {/* Capital residual roll-forward */}
          {hasCapitalChange && capitalResidual !== 0 && (
            <Grid size={12}>
              <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: capitalResidual > 0 ? '#fff3e0' : '#e8f5e9' }}>
                <Typography variant="body2">
                  {capitalResidual > 0
                    ? `Capital residual of R ${formatNumber(capitalResidual, 2)} (deferred) will roll into the capital_amount of the next Combined / Capital Payment row.`
                    : `Capital over-allocation of R ${formatNumber(Math.abs(capitalResidual), 2)} will be deducted from the next Combined / Capital Payment row.`}
                </Typography>
              </Paper>
            </Grid>
          )}

          {hasDateChange && (
            <Grid size={12}>
              <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#e3f2fd' }}>
                <Typography variant="body2">
                  Payment date will move from {originalDate ? format(parseISO(originalDate), 'dd MMM yyyy') : '-'} to {format(parseISO(overrideDate), 'dd MMM yyyy')}.
                  Amounts stay the same unless you also change them.
                </Typography>
              </Paper>
            </Grid>
          )}

          <Grid size={12}>
            <TextField
              label="Reason (Required)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              required
              multiline
              rows={3}
              placeholder="Explain why this override is needed. Captured in the audit trail."
              helperText="A detailed reason is mandatory for schedule overrides"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid || submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : <EditIcon />}
        >
          {submitting
            ? (isApproved ? 'Submitting...' : 'Applying...')
            : (isApproved ? 'Submit for Approval' : 'Apply Override')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleEntryOverrideDialog;
